/**
 * The company's surveillance posture, named field by field — and, since Task U15, a
 * pointer to the screen that edits it.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS PANEL USED TO SAY `wp_policies` HAD NO SESSION ENDPOINT. THAT IS FALSE.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * It was true when Task 45 wrote it: both routes over the table were
 * device-signature authenticated and no task in the plan added one. G13 then shipped
 * `/api/v1/policies` — list, create, patch, activate — and this panel went on telling
 * administrators that the capability did not exist. A screen that denies a capability
 * the API serves is worse than no screen: it is a wrong answer that reads as a
 * considered one, and nobody checks it twice. Tier-3 U15 built the editor
 * (`PolicyEditorScreen`) and this panel now points at it.
 *
 * Two decisions survive the correction, and both are still decisions rather than
 * omissions.
 *
 * **No control here, and specifically not a disabled one.** Not because nothing could
 * work — something can now — but because the editor is one screen away and a second
 * copy of the same form is a second copy to fall out of step. Task 43 ruled it about
 * the export button and Task 44 about the alert slot; the conclusion is the same and
 * `policies.test.tsx` still asserts it directly, because the mutation that adds a
 * half-wired save button here is exactly the one a future task would make.
 *
 * **No values either.** This panel reads no policy. Printing a number beside
 * "collection level" would be this screen inventing a company's surveillance posture,
 * and it would be wrong for every tenant that changed a default. The editor reads
 * them from the server, which is where a number belongs.
 *
 * ── The redaction field carries the fail-closed consequence ─────────────────
 *
 * Task 35 screens every `redaction_patterns` entry statically — at most 200
 * characters, no nested unbounded quantifier, no backreference, must compile with `u`
 * — because the patterns are company-authored and meet an employee-chosen string
 * inside a backtracking engine, and Node has no regex timeout. The screen **fails
 * closed**: if any pattern in a policy is refused, that policy keeps **no** window or
 * page titles at all, company-wide. That is stated here, at the field, because it is
 * the one place a single bad character costs an entire company's title collection —
 * and it is why `PolicyEditorScreen` ships no editor for that field either.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';

const FIELDS: readonly MessageKey[] = [
  'policies.posture.field.level',
  'policies.posture.field.idle',
  'policies.posture.field.windowTitle',
  'policies.posture.field.offHours',
  'policies.posture.field.screenshot',
  'policies.posture.field.keylog',
];

export function PosturePanel(): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="k5-posture-heading" data-testid="k5-posture">
      <h2 className="wp-section__title" id="k5-posture-heading">
        {t('policies.posture.title')}
      </h2>
      <p>{t('policies.posture.body')}</p>
      <p data-testid="k5-posture-no-control">{t('policies.posture.noControl')}</p>
      <p data-testid="k5-posture-no-values">{t('policies.posture.noValues')}</p>

      <ul className="wp-list" data-testid="k5-posture-fields">
        {FIELDS.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
        <li data-testid="k5-posture-redaction">
          {t('policies.posture.field.redaction')}
          <p className="wp-small wp-muted">{t('policies.posture.redaction')}</p>
          <p data-testid="k5-posture-fail-closed">{t('policies.posture.failClosed')}</p>
        </li>
      </ul>

      {/*
        Below the list, not above it, and naming the two exceptions — review round 1,
        Major 3. Above the list this sentence read "the fields below are edited on the
        Collection policy screen", and two of the seven are not: keystroke logging is
        refused by the database and by the API, and the title redaction patterns still
        have no write path from a browser, which the fail-closed sentence two lines up
        already says. A heading that is wrong about the list under it is the exact
        class of statement Task U15 was written to remove from this panel.
      */}
      <p data-testid="k5-posture-where">
        {t('policies.posture.where')}{' '}
        <Link className="wp-linklike" to="/policies/collection" data-testid="k5-posture-link">
          {t('nav.policyCollection')}
        </Link>
      </p>
      <p className="wp-small wp-muted" data-testid="k5-posture-not-editable">
        {t('policies.posture.notEditable')}
      </p>

      {/* Task U12 — the lawful basis this posture rests on, named on the screen
          that names the posture. The grounds are the PDPL's ENUMERATED ones; the
          phrase "legitimate interest" appears nowhere, because it is a GDPR
          balancing test the PDPL does not enumerate (KTD11). */}
      <h3 className="wp-section__title" id="k5-posture-basis-heading">
        {t('policies.posture.lawfulBasis.title')}
      </h3>
      <p data-testid="k5-posture-lawful-basis">{t('policies.posture.lawfulBasis.body')}</p>
      <p className="wp-small wp-muted">{t('policies.posture.lawfulBasis.location')}</p>
      <p className="wp-small wp-muted">{t('policies.posture.lawfulBasis.document')}</p>
    </section>
  );
}
